import { useState, useCallback, useMemo } from "react";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { RequestListRow } from "../components/Requests/RequestListRow";
import { RequestListHeader } from "../components/Requests/RequestListHeader";
import { RequestDetailPanel } from "../components/Requests/RequestDetailPanel";
import RequestFilter from "../components/Requests/RequestFilter";
import {
  useGetContactMessages,
  useUpdateContactReadStatus,
  useMarkContactAsUnread,
} from "../api/contactMessages.queries";
import ClientNavigation from "../components/Client/ClientNavigation";
import type { ContactMessage } from "../types/contact.types";
import { Skeleton } from "@/components/ui/skeleton";

export default function RequestsContainer() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortBy, setSortBy] = useState("newest_first");
  const [filterStatus, setFilterStatus] = useState<"all" | "read" | "unread">(
    "all",
  );
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Calculate skip based on current page
  const skip = (currentPage - 1) * pageSize;

  // Fetch contact messages
  const { data: messagesResponse, isLoading } = useGetContactMessages({
    skip,
    limit: pageSize,
  });

  // Mutation for updating read status
  const { mutate: updateReadStatus } = useUpdateContactReadStatus();

  // Mutation for marking as unread
  const { mutate: markAsUnread } = useMarkContactAsUnread();

  // Filter and sort messages based on status and sort option
  const filteredMessages = useMemo(() => {
    if (!messagesResponse?.data) return [];

    let messages = messagesResponse.data;

    // Filter by status
    if (filterStatus === "read") {
      messages = messages.filter((msg) => msg.is_read);
    } else if (filterStatus === "unread") {
      messages = messages.filter((msg) => !msg.is_read);
    }

    // Sort messages
    const sortedMessages = [...messages];
    switch (sortBy) {
      case "newest_first":
        sortedMessages.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        break;
      case "oldest_first":
        sortedMessages.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
        break;
      case "alphabetical_asc":
        sortedMessages.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "alphabetical_desc":
        sortedMessages.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }

    return sortedMessages;
  }, [messagesResponse?.data, filterStatus, sortBy]);

  // Count messages by status
  const messageCounts = useMemo(() => {
    const all = messagesResponse?.metadata?.total || 0;
    const unread =
      messagesResponse?.data?.filter((msg) => !msg.is_read).length || 0;
    const read = all - unread;
    return { all, unread, read };
  }, [messagesResponse]);

  // Handle toggle read status
  const handleToggleReadStatus = useCallback(
    (messageId: string, currentStatus: boolean) => {
      if (currentStatus) {
        // If currently read, mark as unread
        markAsUnread(messageId);
      } else {
        // If currently unread, mark as read
        updateReadStatus({
          messageId,
          isRead: true,
        });
      }
    },
    [updateReadStatus, markAsUnread],
  );

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredMessages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMessages.map((msg) => msg._id)));
    }
  }, [filteredMessages, selectedIds.size]);

  // Handle individual selection
  const handleSelect = useCallback((messageId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  // Handle mark as read for selected
  const handleMarkSelectedAsRead = useCallback(() => {
    selectedIds.forEach((id) => {
      const message = filteredMessages.find((msg) => msg._id === id);
      if (message && !message.is_read) {
        updateReadStatus({ messageId: id, isRead: true });
      }
    });
    setSelectedIds(new Set());
  }, [selectedIds, filteredMessages, updateReadStatus]);

  // Handle mark as unread for selected
  const handleMarkSelectedAsUnread = useCallback(() => {
    selectedIds.forEach((id) => {
      const message = filteredMessages.find((msg) => msg._id === id);
      if (message && message.is_read) {
        markAsUnread(id);
      }
    });
    setSelectedIds(new Set());
  }, [selectedIds, filteredMessages, markAsUnread]);

  // Handle message click
  const handleMessageClick = useCallback((message: ContactMessage) => {
    setSelectedMessage(message);
  }, []);

  // Handle back from detail view
  const handleBack = useCallback(() => {
    setSelectedMessage(null);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Calculate pagination values
  const metadata = messagesResponse?.metadata;
  const totalItems = metadata?.total || 0;
  const startItem = totalItems > 0 ? skip + 1 : 0;
  const endItem = Math.min(skip + pageSize, totalItems);

  // Loading state
  if (isLoading) {
    return (
      <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <div className="flex flex-col gap-4">
          <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
            <AdminBreadcrumb title="Contact Requests" />
            <Skeleton className="h-10 w-full bg-white/10 md:w-[300px]" />
          </div>
        </div>

        <div className="min-h-0 flex-1">
          <div className="scrollbar-thin grid min-h-0 auto-rows-max gap-4 pb-20">
            <div className="flex w-full flex-col gap-4 lg:flex-row">
              {/* Main content skeleton */}
              <div className="w-full lg:flex-1">
                {/* Header skeleton */}
                <div className="flex flex-col gap-3 px-2 py-3 sm:px-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5 bg-white/10" />
                    <div className="flex gap-2 sm:gap-3">
                      <Skeleton className="h-8 w-8 bg-white/10" />
                      <Skeleton className="h-8 w-8 bg-white/10" />
                      <Skeleton className="h-8 w-8 bg-white/10" />
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Skeleton className="h-8 w-8 bg-white/10" />
                    <Skeleton className="h-4 w-24 bg-white/10" />
                    <Skeleton className="h-8 w-8 bg-white/10" />
                  </div>
                </div>

                {/* List rows skeleton */}
                <div className="mt-3 flex flex-col gap-1">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className="bg-admin-bg flex min-h-[52px] items-center gap-2 px-2 py-3 sm:gap-4 sm:px-5"
                    >
                      <Skeleton className="h-5 w-5 bg-white/10" />
                      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                        <Skeleton className="h-6 w-32 bg-white/10 sm:w-40" />
                        <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                          <Skeleton className="h-5 w-24 bg-white/10" />
                          <Skeleton className="h-5 w-full bg-white/10 sm:max-w-[400px]" />
                        </div>
                        <Skeleton className="h-5 w-32 bg-white/10 sm:w-[180px]" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sidebar skeleton */}
              <div className="hidden lg:block lg:w-[300px]">
                <div className="flex flex-col gap-3">
                  <Skeleton className="h-12 w-full bg-white/10" />
                  <Skeleton className="h-[200px] w-full bg-white/10" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
      <div className="flex flex-col gap-4">
        <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
          <AdminBreadcrumb title="Contact Requests" />
          <ClientNavigation searchQuery="" setSearchQuery={() => {}} />
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <div className="scrollbar-thin grid min-h-0 auto-rows-max gap-4 pb-20">
          {/* Mobile: Show detail panel full screen when message is selected */}
          <div className={selectedMessage ? "block lg:hidden" : "hidden"}>
            {selectedMessage && (
              <RequestDetailPanel
                message={selectedMessage}
                onBack={handleBack}
                onMarkAsUnread={() => markAsUnread(selectedMessage._id)}
              />
            )}
          </div>

          {/* Desktop layout or mobile list view */}
          <div
            className={
              selectedMessage
                ? "hidden lg:flex lg:flex-row lg:gap-4"
                : "flex w-full flex-col-reverse gap-4 xl:flex-row"
            }
          >
            {/* MAIN CONTENT AREA */}
            <div className={selectedMessage ? "lg:flex-1" : "w-full lg:flex-1"}>
              {/* Header with controls and pagination */}
              <RequestListHeader
                selectedCount={selectedIds.size}
                onSelectAll={handleSelectAll}
                onMarkAsRead={handleMarkSelectedAsRead}
                onMarkAsUnread={handleMarkSelectedAsUnread}
                isAllSelected={
                  selectedIds.size === filteredMessages.length &&
                  filteredMessages.length > 0
                }
                currentPage={startItem}
                totalItems={totalItems}
                endItem={endItem}
                onPrevious={() =>
                  handlePageChange(Math.max(1, currentPage - 1))
                }
                onNext={() => handlePageChange(currentPage + 1)}
                hasNext={endItem < totalItems}
                hasPrevious={currentPage > 1}
              />

              {/* Message list */}
              <div className="mt-3 flex min-h-[400px] flex-col gap-1">
                {filteredMessages.length > 0 ? (
                  filteredMessages.map((request) => (
                    <RequestListRow
                      key={request._id}
                      request={request}
                      onToggleReadStatus={handleToggleReadStatus}
                      isSelected={selectedIds.has(request._id)}
                      onSelect={handleSelect}
                      onClick={handleMessageClick}
                      isActive={selectedMessage?._id === request._id}
                    />
                  ))
                ) : (
                  <div className="flex flex-1 items-center justify-center py-12">
                    <p className="text-black-400 text-sm">No messages found</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT SIDEBAR - Detail panel on desktop, filter when no message selected */}
            {selectedMessage ? (
              <div className="animate-in fade-in slide-in-from-right-4 hidden lg:block lg:h-[calc(100vh-270px)] lg:w-[420px] lg:flex-shrink-0">
                <RequestDetailPanel
                  message={selectedMessage}
                  onBack={handleBack}
                  onMarkAsUnread={() => markAsUnread(selectedMessage._id)}
                />
              </div>
            ) : (
              <div className="hidden lg:block lg:w-[300px] lg:flex-shrink-0">
                <RequestFilter
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  filterStatus={filterStatus}
                  onFilterChange={setFilterStatus}
                  statusCounts={messageCounts}
                />
              </div>
            )}
          </div>

          {/* Mobile filter - Show when no message selected */}
          {!selectedMessage && (
            <div className="lg:hidden">
              <RequestFilter
                sortBy={sortBy}
                onSortChange={setSortBy}
                filterStatus={filterStatus}
                onFilterChange={setFilterStatus}
                statusCounts={messageCounts}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
